import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { connect } from "react-redux"

import clsx from "clsx"
import dayjs from "../../../../plugins/dayjs"
import uniqueString from "unique-string"
import { Tooltip } from "react-tooltip"

// core
import API from "../../../../api"
import { ROUTES } from "../../../../routes"
import { mapDispatchProps, mapStateToProps } from "../../../../store/store"
import { debounce } from "../../../../helpers/debounce"
// components
import Button from "../../../../components/button"
import Checkbox from "../../../../components/checkbox"
import TextArea from "../../../../components/textArea"
import TextInputField from "../../../../components/textInput"
import PhotoGuidelineRulesEditor from "../../../../components/photoGuidelineRulesEditor"
import LoadingText from "../../../../components/loadingText"
import GUIDELINE_RULES from "../../../../constants/guidelineRules"

import { useLanguage } from "@languages/index"

const INSTAGRAM_USERNAME_REGEX = /^\@[a-zA-Z0-9_.]{1,30}$/
const USER_TYPE_FL = "0,22,0"

const AmbassadorRequirementsPosting = (props: any) => {
  const groupName_maxLength_map = {
    [GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE]: 100,
    // [GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS]: 50,
  }

  const [shouldBreak, setShouldBreak] = useState(false)

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental, ...language.guidelineTitles }

  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [isStepCompleted, setIsStepCompleted] = useState([null, false])

  const [guidelineRules, setGuidelineRules] = useState<any>([])

  const [isFetching, setIsFetching] = useState(true)
  const [isPhotoGuidelinesEnabled, setIsPhotoGuidelinesEnabled] = useState(true)

  // GUIDELINE STATES
  const [reelsGuidelineRule, setReelsGuidelineRule] = useState({
    id: null,
    value: "",
  })

  const fetchedPhotoGuidelineMapRef = useRef(new Map<any, undefined>())
  const [photoGuidelineMap, setPhotoGuidelineMap] = useState<
    Map<
      any,
      | {
          count?: number
          descriptions?: string[]
        }
      | string
      | undefined
    >
  >(new Map<any, undefined>())

  const [photoGuidelineEtc, setPhotoGuidelineEtc] = useState({
    id: null,
    value: "",
  })

  const fetchedProhibitionsRef = useRef({})
  const [prohibitions, setProhibitions] = useState({})

  const fetchedImageGuidesRef = useRef({ ids: [], values: [] })
  const [guideImages, setGuideImages] = useState({ ids: [], values: [] })

  const [mandatoryPhrase, setMandatoryPhrase] = useState({
    id: null,
    value: "",
  })
  const [atTags, setAtTags] = useState({ id: null, tags: [""] })

  const fetchedAtTagRulesRef = useRef({})
  const [atTagRules, setAtTagRules] = useState({})
  const [hashTags, setHashTags] = useState({ id: null, tags: [""] })

  const fetchedHashTagRulesRef = useRef({})
  const [hashTagRules, setHashTagRules] = useState({})

  const fetchedAdvertisementRulesRef = useRef({})
  const [advertisementRules, setAdvertisementRules] = useState({})

  // 통합 reducer pattern testing
  const [groupNameGuidelineRulesMap, dispatch] = useReducer(
    (state, action) => {
      // common setter
      if (action.type.startsWith("set") && action.type.length > 3) {
        let state_key = action.type.slice(3)
        const key_groupName_map = {
          ReelsGuidelineRule: GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS,
          PhotoGuidelineEtc: GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS,
          MandatoryPhrase: GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE,
          Prohibitions: GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS,
          AtTags: GUIDELINE_RULES.GROUP_NAMES.AT_TAGS,
          HashTags: GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS,
          AtTagRules: GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG,
          HashTagRules: GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION,
          AdvertisementRules: GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG,
          ImageGuides: GUIDELINE_RULES.GROUP_NAMES.GUIDE_IMAGE,
        }
        if (key_groupName_map[state_key] != null) state_key = key_groupName_map[state_key]
        return {
          ...state,
          [state_key]: action.value,
        }
      }
      // no update
      return state
    },
    {
      [GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS]: { id: null, value: "" },
      [GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS]: { id: null, value: "" },
      [GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE]: { id: null, value: "" },
      [GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS]: {},
      [GUIDELINE_RULES.GROUP_NAMES.AT_TAGS]: { id: null, tags: [""] },
      [GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS]: { id: null, tags: [""] },
      [GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG]: {},
      [GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION]: {},
      [GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG]: {},
      [GUIDELINE_RULES.GROUP_NAMES.GUIDE_IMAGE]: { ids: [], values: [] },
    }
  )

  // UseEffect
  useEffect(() => {
    scrollToTop()
  }, [])

  useEffect(() => {
    Promise.all([fetchDefaultGuidelineRules(), fetchBrandGuidelineRules(), fetchBrand()]).then((results) => {
      setIsFetching(false)
    })
  }, [])

  // UX util
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    })
  }

  // FETCH
  const fetchBrand = async () => {
    try {
      const response = await API.Auth.show({ brandToken: props.auth.token })
      props.setBrand(response.data.brand)
    } catch (e) {
      console.error(e)
    }
  }

  const fetchDefaultGuidelineRules = useCallback(async () => {
    try {
      const response = await API.GuidelineRules.create({
        brandToken: props.auth.token,
        userTypeFl: USER_TYPE_FL,
        outputType: GUIDELINE_RULES.OUTPUT_TYPES.PHOTO,
      })
      const rules = response.data.rules.filter((br) => {
        switch (br.groupName) {
          // case GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_FULL:
          // case GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_CLOSEUP:
          //   return false
          default:
            break
        }
        return true
      })
      setGuidelineRules(rules)
    } catch (e) {
      console.error("fetchDefaultGuidelineRules", e)
    }
  }, [props.auth.token])

  const fetchBrandGuidelineRules = useCallback(async () => {
    try {
      const response = await API.Brand.rules({
        brandToken: props.auth.token,
        brandId: props.brand.id,
        outputType: GUIDELINE_RULES.OUTPUT_TYPES.PHOTO,
        userTypeFl: USER_TYPE_FL,
      })
      const rules = response.data.rules

      var hasPhotoGuidelines = false
      var fetchedReelsGuidelineRule = { id: null, value: "" }
      var fetchedPhotoGuidelineMap = {}
      var fetchedPhotoGuidelineEtc = { id: null, value: "" }
      var fetchedProhibitions = {}
      var fetchedImageGuides = { ids: [], values: [] }
      var fetchedAtTags = { id: null, tags: [""] }
      var fetchedAtTagRules = {}
      var fetchedHashTags = { id: null, tags: [""] }
      var fetchedHashTagRules = {}
      var fetchedAdvertisementRules = {}
      var fetchedMandatoryPhrase = { id: null, value: "" }

      for (const rule of rules) {
        // Output
        if (rule["type"] === GUIDELINE_RULES.TYPES.OUTPUT) {
          // Photo
          if (rule["groupName"] !== GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS) {
            if (fetchedPhotoGuidelineMap[rule["groupName"]] == null)
              fetchedPhotoGuidelineMap[rule["groupName"]] = {
                count: 0,
                ids: [],
              }
            fetchedPhotoGuidelineMap[rule["groupName"]]["count"] += 1
            fetchedPhotoGuidelineMap[rule["groupName"]]["ids"].push(rule["id"])
            if (
              [
                GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_CLOSEUP,
                GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_CLOSEUP,
              ].indexOf(rule["groupName"]) >= 0
            ) {
              if (fetchedPhotoGuidelineMap[rule["groupName"]]["descriptions"] == null)
                fetchedPhotoGuidelineMap[rule["groupName"]]["descriptions"] = []
              fetchedPhotoGuidelineMap[rule["groupName"]]["descriptions"].push(rule["value"] ?? "")
            }
            if (!hasPhotoGuidelines) hasPhotoGuidelines = true
            continue
          }
        }

        // Guide
        if (rule["type"] === GUIDELINE_RULES.TYPES.GUIDE) {
          if (rule["value"] == null) continue
          fetchedImageGuides["ids"].push(rule["id"])
          fetchedImageGuides["values"].push(rule["value"])
          continue
        }

        // Prohibitions
        if (rule["type"] === GUIDELINE_RULES.TYPES.PROHIBITIONS) {
          if (rule["value"] == null) continue
          const info = {
            id: rule["id"],
            checked: true,
          }
          switch (rule["groupName"]) {
            case GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS:
              fetchedProhibitions[rule["value"]] = info
              continue
            default:
              continue
          }
        }

        // Texts
        if (rule["type"] === GUIDELINE_RULES.TYPES.TEXTS) {
          switch (rule["groupName"]) {
            case GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS:
              fetchedReelsGuidelineRule = {
                id: rule["id"],
                value: rule["value"] ?? "",
              }
              continue
            case GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS:
              fetchedPhotoGuidelineEtc["id"] = rule["id"]
              fetchedPhotoGuidelineEtc["value"] = rule["value"] ?? ""
              continue
            case GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE:
              fetchedMandatoryPhrase["id"] = rule["id"]
              fetchedMandatoryPhrase["value"] = rule["value"] ?? ""
              continue
            case GUIDELINE_RULES.GROUP_NAMES.AT_TAGS: {
              const tags = new Set<string>()
              if (rule["value"] != null) {
                rule["value"].split(/[\s\r\n]+/).forEach((v) => {
                  tags.add(v)
                })
              }
              fetchedAtTags = {
                id: rule["id"],
                tags: tags.size > 0 ? Array.from(tags) : [""],
              }
              continue
            }
            case GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS: {
              const tags = new Set<string>()
              if (rule["value"] != null) {
                rule["value"].split(/[\s\r\n]+/).forEach((v) => {
                  while (true) {
                    if (v == null || v == "" || (!v.startsWith("@") && !v.startsWith("#"))) break
                    v = v.substring(1)
                  }
                  if (v.length > 0) tags.add(v)
                })
              }
              fetchedHashTags = {
                id: rule["id"],
                tags: tags.size > 0 ? Array.from(tags) : [""],
              }
              continue
            }
            default:
              continue
          }
        }

        // Selections
        if (rule["type"] === GUIDELINE_RULES.TYPES.SELECTIONS) {
          if (rule["value"] == null) continue
          const info = {
            id: rule["id"],
            checked: true,
          }
          switch (rule["groupName"]) {
            case GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG:
              fetchedAtTagRules[rule["value"]] = info
              continue
            case GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION:
              fetchedHashTagRules[rule["value"]] = info
              continue
            case GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG:
              fetchedAdvertisementRules[rule["value"]] = info
              continue
            default:
              continue
          }
        }
      }

      /** DEFAULT VALUES */
      // AT_TAGS
      if (fetchedAtTags.id == null && props.brand.atTag != null) {
        const tags = new Set<string>()
        props.brand.atTag.split(/[\s\r\n]+/).forEach((v) => {
          tags.add(v)
        })
        fetchedAtTags = {
          id: null,
          tags: tags.size > 0 ? Array.from(tags) : [""],
        }
      }

      setReelsGuidelineRule(fetchedReelsGuidelineRule)
      fetchedPhotoGuidelineMapRef.current = new Map(Object.entries(fetchedPhotoGuidelineMap))
      setPhotoGuidelineMap(fetchedPhotoGuidelineMapRef.current)
      // setIsPhotoGuidelinesEnabled(hasPhotoGuidelines); // only for video
      setPhotoGuidelineEtc(fetchedPhotoGuidelineEtc)
      setMandatoryPhrase(fetchedMandatoryPhrase)
      fetchedProhibitionsRef.current = fetchedProhibitions
      setProhibitions(fetchedProhibitions)
      fetchedImageGuidesRef.current = fetchedImageGuides
      setGuideImages(fetchedImageGuides)
      setAtTags(fetchedAtTags)
      setHashTags(fetchedHashTags)
      fetchedAtTagRulesRef.current = fetchedAtTagRules
      setAtTagRules(fetchedAtTagRules)
      fetchedHashTagRulesRef.current = fetchedHashTagRules
      setHashTagRules(fetchedHashTagRules)
      fetchedAdvertisementRulesRef.current = fetchedAdvertisementRules
      setAdvertisementRules(fetchedAdvertisementRules)

      dispatch({
        type: "setPhotoGuidelineEtc",
        value: fetchedPhotoGuidelineEtc,
      })
      dispatch({ type: "setMandatoryPhrase", value: fetchedMandatoryPhrase })
      dispatch({ type: "setProhibitions", value: fetchedProhibitions })
      dispatch({ type: "setGuideImages", value: fetchedImageGuides })
      dispatch({ type: "setAtTags", value: fetchedAtTags })
      dispatch({ type: "setHashTags", value: fetchedHashTags })
      dispatch({ type: "setAtTagRules", value: fetchedAtTagRules })
      dispatch({ type: "setHashTagRules", value: fetchedHashTagRules })
      dispatch({
        type: "setAdvertisementRules",
        value: fetchedAdvertisementRules,
      })
    } catch (e) {
      console.error("fetchBrandGuidelineRules", e)
    }
  }, [])

  // INPUT
  const fileInput = React.useRef(null)

  const handleButtonClick = (e) => {
    fileInput.current.click()
  }

  const handleImageChange = (e) => {
    const image = e.target.files[0]
    e.target.value = ""

    setIsFetching(true)

    API.Guide.uploadImage({
      brandToken: props.auth.token,
      image: image,
    })
      .then((response) => {
        const imageUrl = response.data.url
        let newImageGuides = Object.assign({}, guideImages)
        newImageGuides["values"].push(
          JSON.stringify({
            url: imageUrl,
            description: "",
          })
        )
        setGuideImages(newImageGuides)
      })
      .catch((error) => {})
      .finally(() => {
        setIsFetching(false)
      })
  }

  // SUBMIT
  const groupName_defaultGuidelineRules_map = useMemo(() => {
    if (guidelineRules.length === 0) return {}
    let output = {}
    for (let guidelineRule of guidelineRules) {
      if (guidelineRule["groupName"] == null) continue
      if (output[guidelineRule["groupName"]] == null) output[guidelineRule["groupName"]] = []
      output[guidelineRule["groupName"]].push(guidelineRule)
    }
    return output
  }, [guidelineRules])

  const isCheckRulesValid = useCallback(
    (groupName) => {
      let isMandatory = false
      if (
        groupName_defaultGuidelineRules_map[groupName] != null &&
        groupName_defaultGuidelineRules_map[groupName].length !== 0
      )
        isMandatory = !!groupName_defaultGuidelineRules_map[groupName][0]["isMandatory"]
      if (isMandatory === false) return true

      switch (groupName) {
        case GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS:
          return Object.values(prohibitions).reduce((p, c, i, a) => {
            return p || c["checked"]
          }, false)
          break
        case GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG:
          return Object.values(atTagRules).reduce((p, c, i, a) => {
            return p || c["checked"]
          }, false)
          break
        case GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION:
          return Object.values(hashTagRules).reduce((p, c, i, a) => {
            return p || c["checked"]
          }, false)
          break
        case GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG:
          return Object.values(advertisementRules).reduce((p, c, i, a) => {
            return p || c["checked"]
          }, false)
          break
        default:
          break
      }
    },
    [prohibitions, atTagRules, hashTagRules, advertisementRules, groupName_defaultGuidelineRules_map]
  )

  const validate = useCallback(() => {
    // 릴스
    // if (reelsGuidelineRule == null || reelsGuidelineRule['value'] == null || (reelsGuidelineRule['value'].trim()).length === 0)
    //   return { isValid: false, message: languageTexts.ambassador_requirements_text_30 };

    // 사진
    if (isPhotoGuidelinesEnabled) {
      let hasPhotoGuidelines = false
      for (let groupName of Array.from(photoGuidelineMap.keys())) {
        let v = photoGuidelineMap.get(groupName)
        if (v["count"] != null && v["count"] > 0) {
          hasPhotoGuidelines = true
          break
        }
      }
      if (!hasPhotoGuidelines)
        return {
          isValid: false,
          message: languageTexts.ambassador_requirements_text_87,
        }
    }

    // atTag
    if (atTags == null || atTags["tags"] == null)
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_32,
      }
    if (atTags["tags"].filter((tag) => tag.trim().length !== 0).length == 0)
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_33,
      }

    // hashTag
    if (hashTags == null || hashTags["tags"] == null)
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_34,
      }
    if (hashTags["tags"].filter((tag) => tag.trim().length !== 0).length == 0)
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_35,
      }

    // Checks
    if (!isCheckRulesValid(GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS))
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_36,
      }
    if (!isCheckRulesValid(GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG))
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_37,
      }
    if (!isCheckRulesValid(GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION))
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_38,
      }
    if (!isCheckRulesValid(GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG))
      return {
        isValid: false,
        message: languageTexts.ambassador_requirements_text_39,
      }

    return {
      isValid: true,
      message: languageTexts.ambassador_requirements_text_40,
    }
  }, [
    reelsGuidelineRule,
    photoGuidelineMap,
    photoGuidelineEtc,
    prohibitions,
    guideImages,
    atTags,
    atTagRules,
    hashTags,
    hashTagRules,
    advertisementRules,
    isPhotoGuidelinesEnabled,
    isCheckRulesValid,
  ])

  const makeParamPhotoRules = useCallback(
    (origin, outputType) => {
      let outputs = []
      ;[
        GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_FULL,
        GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_HALF,
        GUIDELINE_RULES.GROUP_NAMES.PHOTO_MODEL_CLOSEUP,
        GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_FULL,
        GUIDELINE_RULES.GROUP_NAMES.PHOTO_PRODUCT_CLOSEUP,
      ].forEach((groupName) => {
        let status = origin.get(groupName)
        if (status == null) return
        let fetchedIds = status["ids"] ?? []
        let descriptions = status["descriptions"] ?? []
        for (let i = 0; i < Math.max(status["count"], fetchedIds.length); i++) {
          let param = {
            outputType,
            type: GUIDELINE_RULES.TYPES.OUTPUT,
            groupName,
            userTypeFl: USER_TYPE_FL,
            order: i + 1,
          }
          let id = fetchedIds[i]
          let value = descriptions[i]
          if (id != null) param["id"] = id
          if (value != null) param["value"] = value

          // all disabled - remove all
          if (!isPhotoGuidelinesEnabled) {
            if (id != null) {
              param["deletedAt"] = dayjs().toISOString()
              outputs.push(param)
            }
            continue
          }

          // add or update case
          if (i <= status["count"] - 1) {
            outputs.push(param)
            continue
          }

          // remove case
          if (id != null) {
            param["deletedAt"] = dayjs().toISOString()
            outputs.push(param)
          }
        }
      })
      return outputs
    },
    [isPhotoGuidelinesEnabled]
  )

  const makeParamTextRules = useCallback(
    (origin, outputType, groupName) => {
      if (origin == null || (origin["id"] == null && origin["value"] === null)) return []

      let value = origin["value"].trim()
      let param = {
        outputType,
        type: GUIDELINE_RULES.TYPES.TEXTS,
        groupName,
        userTypeFl: USER_TYPE_FL,
        value,
      }
      if (origin["id"] != null) param["id"] = origin["id"]

      // REELS OPTIONAL OTHER_REQUESTS
      if (!isPhotoGuidelinesEnabled && groupName === GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS) {
        if (param["id"] != null) {
          param["deletedAt"] = dayjs().toISOString()
          return [param]
        }
        return []
      }

      return [param]
    },
    [isPhotoGuidelinesEnabled]
  )

  const makeParamGuideImageRules = useCallback((origin, outputType, groupName) => {
    if (origin == null || origin["values"] == null) return []
    let output = []
    let ids = origin["ids"] || []
    for (let i = 0; i < Math.max(ids.length, origin["values"].length); i++) {
      let param = {
        outputType,
        type: GUIDELINE_RULES.TYPES.GUIDE,
        groupName: GUIDELINE_RULES.GROUP_NAMES.GUIDE_IMAGE,
        userTypeFl: USER_TYPE_FL,
        order: i + 1,
      }
      let id = ids[i]
      let value = origin["values"][i]
      try {
        JSON.parse(value)
      } catch (e) {
        if (value != null && value.length > 0) {
          value = JSON.stringify({
            url: value,
            description: "",
          })
        }
      }
      if (id != null) {
        param["id"] = id
      }

      // remove
      if (value == null || value.length === 0) {
        param["deletedAt"] = dayjs().toISOString()
        output.push(param)
        continue
      }

      // add or update
      param["value"] = value
      output.push(param)
    }
    return output
  }, [])

  const makeParamProhibitionRules = useCallback(
    (origin, outputType, type, groupName) => {
      if (origin == null) return []
      let output = []

      let valueOrderMap = {}
      guidelineRules
        .filter((gr) => gr["groupName"] == groupName)
        .forEach((gr) => {
          valueOrderMap[gr["value"]] = gr["order"]
        })
      for (let value of Object.keys(origin)) {
        let param = {
          outputType,
          type,
          groupName,
          userTypeFl: USER_TYPE_FL,
          value,
          order: valueOrderMap[value] ?? null,
        }
        let id = origin[value]["id"]
        let isChecked = origin[value]["checked"]
        if (id != null) param["id"] = id

        if (!isChecked) {
          // remove
          if (id != null) {
            param["deletedAt"] = dayjs().toISOString()
            output.push(param)
          }
          continue
        }

        // add or update
        output.push(param)
      }
      return output
    },
    [guidelineRules]
  )

  const makeParamSelectRules = useCallback((origin, outputType, type, groupName) => {
    if (origin == null) return []
    let output = []
    for (let value of Object.keys(origin)) {
      let param = {
        outputType,
        type,
        groupName,
        userTypeFl: USER_TYPE_FL,
        value,
      }
      let id = origin[value]["id"]
      let isChecked = origin[value]["checked"]
      if (id != null) param["id"] = id

      if (!isChecked) {
        // remove
        if (id != null) {
          param["deletedAt"] = dayjs().toISOString()
          output.push(param)
        }
        continue
      }

      // add or update
      output.push(param)
    }
    return output
  }, [])

  /**
   * @param {string} prefix - `@` or `#`
   */
  const makeParamTagRules = useCallback((origin, outputType, groupName, prefix = "") => {
    if (origin == null || origin["tags"] == null) return []
    const tags = origin["tags"].filter((t) => t != null && t.trim().length > 0)
    if (tags.length === 0) return []
    let value = tags
      .map((t) => {
        if (prefix == null || prefix.length === 0) return t.trim()
        if (t.startsWith(prefix)) return t.trim()
        return prefix + t.trim()
      })
      .join(" ")

    let param = {
      outputType,
      type: GUIDELINE_RULES.TYPES.TEXTS,
      groupName,
      userTypeFl: USER_TYPE_FL,
      value,
    }
    if (origin["id"] != null) param["id"] = origin["id"]
    return [param]
  }, [])

  const showUpdateWarningModal = useCallback((callback) => {
    props.showModal({
      title: languageTexts.ambassador_requirements_text_82,
      text: languageTexts.ambassador_requirements_text_83,
      confirmText: languageTexts.ambassador_requirements_text_84,
      confirm: () => {
        callback ? callback() : null
      },
      cancel: () => {},
    })
  }, [])

  const submit = useCallback(async () => {
    const runSubmit = async () => {
      try {
        setIsFetching(true)
        const response = await API.GuidelineRules.updatesAmbassadorRules({
          brandToken: props.auth.token,
          userTypeFl: USER_TYPE_FL,
          outputType: outputType,
          rules: paramGuidelineRules,
        })

        await Promise.all([fetchDefaultGuidelineRules(), fetchBrandGuidelineRules()]).then((results) => {
          setIsFetching(false)
          props.showModal({
            hideCloseButton: true,
            isCompact: true,
            text: languageTexts.ambassador_requirements_text_85,
          })
        })
      } catch (e) {
        console.error(e)
        setIsFetching(false)
        props.showModal({
          hideCloseButton: true,
          isCompact: true,
          text: languageTexts.ambassador_requirements_text_86,
        })
      }
    }

    let outputType = GUIDELINE_RULES.OUTPUT_TYPES.PHOTO // 사진

    let paramGuidelineRules = [].concat.apply(
      [],
      [
        // makeParamTextRules(
        //   reelsGuidelineRule,
        //   outputType,
        //   GUIDELINE_RULES.GROUP_NAMES.REELS_REQUESTS
        // ),
        makeParamPhotoRules(photoGuidelineMap, outputType),
        makeParamTextRules(photoGuidelineEtc, outputType, GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS),
        makeParamGuideImageRules(guideImages, outputType, GUIDELINE_RULES.GROUP_NAMES.GUIDE_IMAGE),
        makeParamProhibitionRules(
          prohibitions,
          outputType,
          GUIDELINE_RULES.TYPES.PROHIBITIONS,
          GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS
        ),
        makeParamTextRules(mandatoryPhrase, outputType, GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE),
        makeParamTagRules(atTags, outputType, GUIDELINE_RULES.GROUP_NAMES.AT_TAGS, "@"),
        makeParamSelectRules(
          atTagRules,
          outputType,
          GUIDELINE_RULES.TYPES.SELECTIONS,
          GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG
        ),
        makeParamTagRules(hashTags, outputType, GUIDELINE_RULES.GROUP_NAMES.HASH_TAGS, "#"),
        makeParamSelectRules(
          hashTagRules,
          outputType,
          GUIDELINE_RULES.TYPES.SELECTIONS,
          GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION
        ),
        makeParamSelectRules(
          advertisementRules,
          outputType,
          GUIDELINE_RULES.TYPES.SELECTIONS,
          GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG
        ),
      ]
    )

    if (shouldBreak) return

    if (props.brand.postingNotFinishedCountTypeRental) {
      showUpdateWarningModal(runSubmit)
      return false
    }

    runSubmit()
  }, [
    reelsGuidelineRule,
    photoGuidelineMap,
    photoGuidelineEtc,
    prohibitions,
    guideImages,
    mandatoryPhrase,
    atTags,
    atTagRules,
    hashTags,
    hashTagRules,
    advertisementRules,
    isPhotoGuidelinesEnabled,
    shouldBreak,
  ])

  // RENDER
  const renderAddTagButton = () => {
    return (
      <>
        <Button
          className="border-1 border-gray-200 hover:border-black text-center py-3"
          justify={"center"}
          text={"+"}
          onClick={() => {
            setHashTags({
              ...hashTags,
              tags: [...hashTags["tags"], ""],
            })
          }}
        />
      </>
    )
  }

  const renderImageGuideCreateButton = () => {
    return (
      <div
        className="flex justify-center items-center cursor-pointer mx-auto py-3 w-full"
        style={{
          border: "1px solid #E8E8E8",
        }}
        onClick={handleButtonClick}
      >
        <div className="">+</div>
        <input type="file" ref={fileInput} onChange={handleImageChange} style={{ display: "none" }} />
      </div>
    )
  }

  const renderImageGuide = () => {
    return guideImages["values"].map((value, index) => {
      let parsedValue
      try {
        parsedValue = JSON.parse(value)
      } catch (e) {
        parsedValue = { url: value, description: "" }
      }

      return (
        <ImageGuide
          key={`campaign-images-${index}`}
          index={index}
          image={parsedValue["url"]}
          description={parsedValue["description"]}
          onUpdate={(desc) => {
            parsedValue["description"] = desc
            let copy = guideImages
            copy["values"][index] = JSON.stringify(parsedValue)
            setGuideImages(copy)
          }}
          onDelete={() => {
            props.showModal({
              hideCloseButton: true,
              isCompact: true,
              text: languageTexts.ambassador_requirements_text_43,
              cancel: () => {},
              confirm: () => {
                let values = [...guideImages["values"]]
                values = values.filter((v) => {
                  return v !== value
                })
                setGuideImages({
                  ids: guideImages["ids"],
                  values: values,
                })
                debounce(() => {
                  props.showModal({
                    hideCloseButton: true,
                    isCompact: true,
                    text: languageTexts.ambassador_requirements_text_44,
                    confirm: () => {},
                  })
                }, 500)

                return
                // S3의 데이터를 즉시 지우는 과정이 있다면 API 요청 추가 (현재는 값만 지우는 듯)
                // if (imageGuide['id']) {
                //   // delete existing image
                //   API.Guide.delete({ id: imageGuide['id'] })
                //     .then((response) => {
                //       setTimeout(() => {
                //         props.showModal({
                //           text: languageTexts.ambassador_requirements_text_45,
                //           confirm: () => {
                //             fetchImages();
                //           },
                //         });
                //       }, 400);
                //     })
                //     .catch((error) => {
                //       setTimeout(() => {
                //         props.showModal({
                //           text: error.message,
                //         });
                //       }, 400);
                //     });
                // } else {
                //   // delete recently added image
                //   let copy = [...guideImages];
                //   copy = copy.filter((element) => {
                //     console.log(element['value'] !== imageGuide['value'], element['value'], imageGuide['value'])
                //     return element['value'] !== imageGuide['value'];
                //   });

                //   setGuideImages(copy);

                //   props.showModal({
                //     text: languageTexts.ambassador_requirements_text_45,
                //     confirm: () => {
                //     },
                //   });
                // }
              },
            })
          }}
        />
      )
    })
  }

  const renderForm = () => {
    const formTitleStyle = {
      fontSize: "16px",
      lineHeight: "19px",
      letterSpacing: "-0.05em",
      fontWeight: 700,
    }

    const formDescStyle = {
      fontSize: "14px",
      lineHeight: "17px",
      letterSpacing: "-0.05em",
      color: "#999999",
    }

    const formSubtitleStyle = {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "17px",
      letterSpacing: "-0.05em",
      whiteSpace: "break-spaces" as "break-spaces",
    }

    const renderSectorTitle = (title, marginClassName = "mt-[60px]") => {
      return <div className={["text-[22px] font-bold", marginClassName].join(" ")}>{title}</div>
    }

    const renderRuleChecks = ({ groupName, title, description = null, reference = null, state, setState }) => {
      const handleCheckbox = (value, isMultiple, isMandatory, guidelineRules) => {
        const hasValue = state[value] != null
        const isChecked = hasValue && state[value]["checked"] === true
        if (isMandatory && isChecked) {
          props.showModal({
            hideCloseButton: true,
            isCompact: true,
            text: languageTexts.ambassador_requirements_text_46,
          })
        }

        const newState = Object.assign({}, state)
        const every_values = guidelineRules.map((gr) => gr["value"])

        // not multiple
        if (!isMultiple) {
          for (let value in newState) {
            if (every_values.indexOf(value) >= 0) delete newState[value]
          }
          let id = null
          if (reference != null) {
            for (let value in reference) {
              if (reference[value] != null && reference[value]["id"] != null) {
                id = reference[value]["id"]
                break
              }
            }
          }
          setState({
            ...newState,
            [value]: {
              id,
              checked: !isChecked,
            },
          })
          return
        }

        // multiple
        setState({
          ...newState,
          [value]: {
            id: reference != null && reference[value] != null ? reference[value]["id"] : null,
            checked: !isChecked,
          },
        })
      }

      if (guidelineRules == null || guidelineRules.length <= 0) return null

      let guidelineRulesOfGroup = guidelineRules.filter((gr) => gr["groupName"] == groupName)
      if (guidelineRulesOfGroup.length <= 0) return null

      const isMultiple = guidelineRulesOfGroup[0]["isMultiple"]
      const isMandatory = guidelineRulesOfGroup[0]["isMandatory"]

      return (
        <div className="at-tag-form mt-7">
          <div className="form-title">
            <span style={formSubtitleStyle}>{title}</span>
          </div>
          <div className="form-description mt-2">
            <span style={formDescStyle}>{description}</span>
          </div>
          <div className="form-input mt-[10px]">
            <div className="flex flex-col">
              {guidelineRulesOfGroup.map((fgr, index) => {
                const value = fgr["value"]
                const is_checked = state[value] != null && state[value]["checked"] === true

                return (
                  <div key={`checkbox-container-${index}`}>
                    <div className="flex justify-start items-center gap-2">
                      <Checkbox
                        key={index}
                        className={"py-[6px] select-none"}
                        textClassName={"select-none"}
                        text={GUIDELINE_RULES.GetOptionTranslationMap(groupName, value)}
                        checked={is_checked}
                        onClick={() => handleCheckbox(value, isMultiple, isMandatory, guidelineRulesOfGroup)}
                      />

                      {/* TOOLTIP PARTS */}
                      {fgr["groupName"] === GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG &&
                        fgr["value"] === GUIDELINE_RULES.OPTIONS_ALLOW_OTHER_AT_TAG.ALLOW && (
                          <>
                            <div className="flex items-center gap-1">
                              <img
                                data-tooltip-id={`tooltip-allow-other-at-tags`}
                                className={"w-[14px] h-[14px] select-none cursor-pointer"}
                                src={"/assets/icons/ic-question-mark-black.svg"}
                              />
                              <div
                                data-tooltip-id={`tooltip-allow-other-at-tags`}
                                className="px-[5px] py-[4px] text-[#000000] bg-[#5D5FEF33] text-[11px] font-bold rounded-md select-none cursor-pointer"
                              >
                                {languageTexts.ambassador_requirements_text_88}
                              </div>
                            </div>
                            <Tooltip
                              id={`tooltip-allow-other-at-tags`}
                              style={{
                                // width: `${props.tooltipWidth}px`,
                                // maxWidth: `${props.tooltipWidth}px`,
                                justifyContent: "flex-start",
                                textAlign: "start",
                              }}
                            >
                              <p className="text-xs whitespace-pre-wrap">
                                {languageTexts.ambassador_requirements_text_89}
                              </p>
                            </Tooltip>
                          </>
                        )}
                    </div>

                    {fgr["groupName"] === GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS &&
                      fgr["value"] === GUIDELINE_RULES.OPTIONS_PROHIBITIONS.INDOORS && (
                        <div className="pl-8 text-xs text-[#959595]">
                          {languageTexts.ambassador_requirements_text_90}
                        </div>
                      )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    }

    function sliceTextByGroupName(value, groupName) {
      const maxLength = groupName_maxLength_map[groupName]
      if (value.length > maxLength) {
        value = value.slice(0, maxLength)
      }
      return value
    }

    function makeMaxLengthTextOfGroupName(value, groupName) {
      return languageTexts._ambassador_requirements_text_91(value.length, groupName_maxLength_map[groupName])
    }

    const validation = validate()

    return (
      <div className="flex flex-col">
        <div className="text-[32px] font-bold">{languageTexts.ambassador_requirements_text_47}</div>

        {renderSectorTitle(languageTexts.ambassador_requirements_text_48, "mt-[71px]")}

        <div className="at-tag-form mt-[30px]">
          <div className="form-title">
            <span style={formSubtitleStyle}>{languageTexts.ambassador_requirements_text_49}</span>
          </div>
          <div className="form-description mt-2">
            <span style={formDescStyle}>{languageTexts.ambassador_requirements_text_50}</span>
          </div>

          <PhotoGuidelineRulesEditor
            {...props}
            guidelines={[
              {
                groupName: "PHOTO_MODEL_FULL",
                text: languageTexts.PHOTO_MODEL_FULL,
                hasValue: false,
              },
              {
                groupName: "PHOTO_MODEL_HALF",
                text: languageTexts.PHOTO_MODEL_HALF,
                hasValue: false,
              },
              {
                groupName: "PHOTO_MODEL_CLOSEUP",
                text: languageTexts.PHOTO_MODEL_CLOSEUP,
                hasValue: true,
              },
              // {},
              // {
              //   groupName: "PHOTO_PRODUCT_FULL",
              //   text: languageTexts.PHOTO_PRODUCT_FULL,
              //   hasValue: false
              // },
              // {
              //   groupName: "PHOTO_PRODUCT_CLOSEUP",
              //   text: languageTexts.PHOTO_PRODUCT_CLOSEUP,
              //   hasValue: true,
              // },
            ]}
            photoGuidelineMap={photoGuidelineMap}
            fetchedPhotoGuidelineMap={fetchedPhotoGuidelineMapRef.current}
            photoGuidelineEtc={photoGuidelineEtc}
            setPhotoGuidelineMap={setPhotoGuidelineMap}
            setPhotoGuidelineEtc={(value) => {
              setPhotoGuidelineEtc(value)
              dispatch({ type: "setPhotoGuidelineEtc", value })
            }}
            otherRequestsMaxLength={groupName_maxLength_map[GUIDELINE_RULES.GROUP_NAMES.OTHER_REQUESTS]}
          />
        </div>

        {renderRuleChecks({
          groupName: GUIDELINE_RULES.GROUP_NAMES.PROHIBITIONS,
          title: languageTexts.ambassador_requirements_text_56,
          reference: fetchedProhibitionsRef.current,
          state: prohibitions,
          setState: (newState) => {
            setProhibitions(newState)
            dispatch({ type: "setProhibitions", value: newState })
          },
        })}

        <div className="at-tag-form mt-7">
          <div className="form-title">
            <span style={formSubtitleStyle}>{languageTexts.ambassador_requirements_text_57}</span>
          </div>
          <div className="form-description mt-2">
            <span style={formDescStyle}>{languageTexts.ambassador_requirements_text_58}</span>
          </div>
          <div className="form-input mt-4">
            <div className="gap-5 flex flex-col">
              {renderImageGuide()}
              {renderImageGuideCreateButton()}
            </div>
          </div>
        </div>

        {renderSectorTitle(languageTexts.ambassador_requirements_text_59)}

        <div className="mandatory-phrase-form mt-[30px]">
          <div className="form-title" style={formSubtitleStyle}>
            {languageTexts.ambassador_requirements_text_60}
          </div>
          <div className="form-description mt-2 flex items-center">
            <div style={formDescStyle}>{languageTexts.ambassador_requirements_text_61}</div>
            <div className="text-xs font-normal text-black whitespace-pre">
              {`  ${makeMaxLengthTextOfGroupName(
                mandatoryPhrase?.value ?? "",
                GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE
              )}`}
            </div>
          </div>
          <div className="form-input mt-4">
            <TextInputField
              bordered
              inputStyle={{ fontSize: "14px" }}
              placeholder={languageTexts.ambassador_requirements_text_62}
              value={mandatoryPhrase?.value ?? ""}
              onChangeText={(value) => {
                setMandatoryPhrase({
                  ...mandatoryPhrase,
                  value: sliceTextByGroupName(value, GUIDELINE_RULES.GROUP_NAMES.MANDATORY_PHRASE),
                })
              }}
            />
          </div>
        </div>

        {renderSectorTitle(languageTexts.ambassador_requirements_text_63)}

        <div className="at-tag-form mt-[30px]">
          <div className="form-title">
            <span style={formTitleStyle}>{languageTexts.ambassador_requirements_text_64}</span>
          </div>
          <div className="form-input mt-4 gap-2.5 flex flex-col">
            {atTags &&
              atTags["tags"] &&
              atTags["tags"].map((inputs, index) => {
                return (
                  <TextInputField
                    key={index}
                    bordered
                    inputStyle={{ fontSize: "14px" }}
                    value={inputs}
                    onChangeText={(value) => {
                      let copy = [...(atTags["tags"] ?? [""])]
                      if (value.length > 0) {
                        copy.forEach((prev, copyIndex) => {
                          if (index === copyIndex) {
                            copy[index] = value.trim()
                          }
                        })
                      } else {
                        copy = copy.filter((value, copyIndex) => {
                          return copyIndex !== index
                        })
                        if (copy.length === 0) copy = [""]
                      }
                      setAtTags({
                        ...atTags,
                        tags: copy,
                      })
                      dispatch({
                        type: "setAtTags",
                        value: {
                          ...atTags,
                          tags: copy,
                        },
                      })
                    }}
                    placeholder={`${props.brand.atTag ? props.brand.atTag : "@brand_tag"}`}
                  />
                )
              })}
            <Button
              className="border-1 border-gray-200 hover:border-black text-center py-3"
              justify={"center"}
              text={"+"}
              onClick={() => {
                var has_empty_string_already = false
                if (atTags != null && atTags["tags"] != null && atTags["tags"].length > 0) {
                  atTags["tags"].forEach((v) => {
                    if (v.length === 0 || v === "@") has_empty_string_already = true
                  })
                }
                if (has_empty_string_already) return
                setAtTags({
                  ...atTags,
                  tags: [...atTags["tags"], ""],
                })
                dispatch({
                  type: "setAtTags",
                  value: {
                    ...atTags,
                    tags: [...atTags["tags"], ""],
                  },
                })
              }}
            />
          </div>
        </div>

        {renderRuleChecks({
          groupName: GUIDELINE_RULES.GROUP_NAMES.ALLOW_OTHER_AT_TAG,
          title: languageTexts.ambassador_requirements_text_65,
          description: languageTexts.ambassador_requirements_text_66,
          reference: fetchedAtTagRulesRef.current,
          state: atTagRules,
          setState: (newState) => {
            setAtTagRules(newState)
            dispatch({ type: "setAtTagRules", value: newState })
          },
        })}

        <div className="hash-tag-form mt-[30px]">
          <div className="form-title">
            <span style={formSubtitleStyle}>
              {languageTexts.ambassador_requirements_text_67(props.brand.categoryId)}
            </span>
          </div>
          <div className="form-input mt-4 mb-7 gap-2.5 flex flex-col">
            <TextInputField
              bordered
              inputStyle={{ fontSize: "14px" }}
              value={hashTags["tags"][0] ?? ""}
              onChangeText={(value) => {
                let copy = [...hashTags["tags"]]
                copy[0] = value.trim()
                setHashTags({
                  ...hashTags,
                  tags: copy,
                })
                dispatch({
                  type: "setHashTags",
                  value: {
                    ...hashTags,
                    tags: copy,
                  },
                })
              }}
              placeholder={languageTexts.ambassador_requirements_text_68}
            />

            <TextInputField
              bordered
              inputStyle={{ fontSize: "14px" }}
              value={hashTags["tags"][1] ?? ""}
              onChangeText={(value) => {
                let copy = [...hashTags["tags"]]
                copy[1] = value.trim()
                setHashTags({
                  ...hashTags,
                  tags: copy,
                })
                dispatch({
                  type: "setHashTags",
                  value: {
                    ...hashTags,
                    tags: copy,
                  },
                })
              }}
              placeholder={languageTexts.ambassador_requirements_text_68}
            />
          </div>

          <div className="hash-tag-form">
            <div className="form-title">
              <span style={formTitleStyle}>{languageTexts.ambassador_requirements_text_69}</span>
            </div>
            <div className="form-input mt-4 gap-2.5 flex flex-col">
              {hashTags["tags"].map((inputs, index) => {
                return index > 1 ? (
                  <TextInputField
                    key={index}
                    bordered
                    inputStyle={{ fontSize: "14px" }}
                    value={inputs}
                    onChangeText={(value) => {
                      if (value) {
                        let copy = [...hashTags["tags"]]
                        copy.forEach((prev, copyIndex) => {
                          if (index === copyIndex) {
                            copy[index] = value.trim()
                          }
                        })
                        setHashTags({
                          ...hashTags,
                          tags: copy,
                        })
                      } else {
                        let copy = [...hashTags["tags"]]
                        copy = copy.filter((value, copyIndex) => {
                          return copyIndex !== index
                        })
                        setHashTags({
                          ...hashTags,
                          tags: copy,
                        })
                      }
                    }}
                    placeholder={languageTexts.ambassador_requirements_text_70}
                  />
                ) : null
              })}
              {renderAddTagButton()}
            </div>
          </div>
        </div>

        {renderRuleChecks({
          groupName: GUIDELINE_RULES.GROUP_NAMES.HASH_TAG_LOCATION,
          title: languageTexts.ambassador_requirements_text_71,
          reference: fetchedHashTagRulesRef.current,
          state: hashTagRules,
          setState: (newState) => {
            setHashTagRules(newState)
            dispatch({ type: "setHashTagRules", value: newState })
          },
        })}

        {renderRuleChecks({
          groupName: GUIDELINE_RULES.GROUP_NAMES.ALLOW_ADVERTISEMENT_TAG,
          title: languageTexts.ambassador_requirements_text_72,
          reference: fetchedAdvertisementRulesRef.current,
          state: advertisementRules,
          setState: (newState) => {
            setAdvertisementRules(newState)
            dispatch({ type: "setAdvertisementRules", value: newState })
          },
        })}

        <div className="mt-[60px]">
          {/* TEST CODE */}
          {/* <div className="mb-[20px]">
            <Checkbox
              text={"제출없이 Params 로그만 찍기"}
              checked={shouldBreak}
              onClick={() => setShouldBreak(!shouldBreak)}
            />
          </div> */}
          <Button
            text={languageTexts.done}
            textColor={"WHITE"}
            className="md:min-w-[240px] px-4 py-3 justify-center text-center"
            buttonColor={validation["isValid"] ? "BLACK" : "#919191"}
            borderColor={validation["isValid"] ? "BLACK" : "#919191"}
            onClick={() => {
              if (!validation["isValid"]) {
                props.showModal({
                  hideCloseButton: true,
                  isCompact: true,
                  text: validation["message"],
                })
                return false
              }

              submit()
            }}
          />
        </div>

        <div className="pb-[100px]" />
      </div>
    )
  }

  const ImageGuide = (props: any) => {
    let className = clsx(
      "guide-image relative flex flex-col justify-center items-center cursor-pointer -z-[1]",
      `${props.className ? props.className : ""}`
    )

    const renderDeleteButton = () => {
      return (
        <div
          className="absolute cursor-pointer"
          style={{
            top: 0,
            right: 0,
            zIndex: 1,
            backgroundColor: "black",
          }}
          onClick={() => {
            props.onDelete ? props.onDelete() : null
          }}
        >
          <img className="p-1" src={"/assets/icons/close-white.svg"} />
        </div>
      )
    }

    const renderDescriptionInput = () => {
      const [value, setValue] = useState(props.description)
      const language = useLanguage()
      const languageTexts = { ...language.common }
      return (
        <div className="mt-5">
          <TextInputField
            className="relative"
            key={`guide-input-${props.index}`}
            placeholder={languageTexts.one_line_desc}
            value={value}
            onChangeText={(v) => setValue(v)}
            button={{
              condition: "on-focus",
              position: "right",
              button: () => {
                return (
                  <div className="absolute top-0 bottom-0 -right-[50px] w-[50px] flex items-center pr-1">
                    <Button
                      className={"rounded px-3 py-2"}
                      text={languageTexts.save}
                      textClassName={"text-sm tracking-tighter"}
                      textColor="WHITE"
                      borderColor={"BLACK"}
                      buttonColor={"BLACK"}
                      onClick={() => {
                        props.onUpdate(value)
                      }}
                    />
                  </div>
                )
              },
            }}
          />
        </div>
      )
    }

    return (
      <div className="flex flex-col relative">
        {renderDeleteButton()}

        <div
          className={className}
          style={{
            border: "1px solid #E8E8E8",
          }}
        >
          <img
            className="object-cover"
            src={props.image}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        {renderDescriptionInput()}
      </div>
    )
  }

  // console.log("---TEST CODES ---");
  // console.groupCollapsed("states");
  // console.log(reelsGuidelineRule);
  // console.log(photoGuidelineMap);
  // console.log(photoGuidelineEtc);
  // console.log(prohibitions);
  // console.log(guideImages);
  // console.log(mandatoryPhrase);
  // console.log(atTags);
  // console.log(atTagRules);
  // console.log(hashTags);
  // console.log(hashTagRules);
  // console.log(advertisementRules);
  // console.groupEnd();
  // console.log(groupNameGuidelineRulesMap);
  // console.log(groupName_defaultGuidelineRules_map)

  return (
    <div
      className="relative w-full flex flex-col items-center justify-center"
      style={{ paddingTop: props.preferences.headerHeight }}
    >
      {!isFetching ? null : (
        <div className="fixed top-0 w-full h-full flex items-center justify-center bg-slate-400 opacity-50 z-[1]">
          <LoadingText
            text={"Loading"}
            render={(text) => (
              <div className="w-[180px] rounded-full p-2 text-center text-2xl font-bold text-white select-none">
                {text}
              </div>
            )}
          />
        </div>
      )}
      <div className="flex flex-col mx-auto max-w-[450px] my-10 pt-5">{renderForm()}</div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(AmbassadorRequirementsPosting)
